import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    IconChevronLeft, IconChevronRight, IconPlus, IconTrash, IconSparkles,
    IconCheck, IconX, IconUpload, IconLoader2, IconEye, IconBolt, IconAlertTriangle, IconCreditCard,
    IconSquare, IconSquareCheckFilled, IconPhoto, IconDeviceMobileMessage, IconShirt, IconWand,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import bulkPhotoService from '../../../services/bulkPhotoService';
import PhotoBoothInbox from '../../../components/common/PhotoBoothInbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { creditParts } from '../../../utils/credit';
import { useBuyCreditPath } from '../../../store/paymentConfigStore';
import { useMediaQuery } from '../../../lib/hooks';
// The Studio Editor's own controls — each photo gets the same background / merge, dress and tools as a studio edit.
import { BackgroundPicker } from '../studio-editor/StudioControls';
import StudioDressSection, { useStudioDressOptions } from '../studio-editor/StudioDressSection';
import StudioToolsPanel from '../studio-editor/StudioToolsPanel';
import { BG_BLENDS, DEFAULT_DRESS, KEEP_BG, isValidBg, parseDress, studioDressRequest } from '../studio-editor/studioConstants';

// Representative accent color for a style, pulled from its gradient `swatch`
// (uses the deeper, more saturated stop). Drives the card + preview borders.
const swatchColor = (th) => {
    const m = (th?.swatch || '').match(/rgb\([^)]+\)/g);
    return (m && (m[1] || m[0])) || '#E68A00';
};
const rgba = (color, a) =>
    color.startsWith('rgb(') ? color.replace('rgb(', 'rgba(').replace(')', `, ${a})`) : color;
const SECONDS_PER_IMAGE = 120; // batch mode averages ~2 min/image — estimate shown in the confirm modal
const MAX_BATCH_HOURS = 12; // upper bound of the estimated-time range in the confirm modal
const MIN_IMAGES = 3; // batch requires at least this many photos before it can start
const MAX_FILE_MB = 10; // reject uploads bigger than this at pick time
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

// The panel's Studio-style settings: who is in the photo, their dresses, the background (or the photo's own), the merge and the
// tools ({ toolId: level|null }). Applying them stamps a copy on each selected photo.
const newDresses = () => ({ single: DEFAULT_DRESS, left: DEFAULT_DRESS, right: DEFAULT_DRESS, men: DEFAULT_DRESS, women: DEFAULT_DRESS, children: DEFAULT_DRESS });
const EMPTY_SETTINGS = { people: 'single', bg: KEEP_BG, bgBlend: '', dresses: newDresses(), tools: {} };
const peopleDresses = (s) => ({ dual: [s.dresses.left, s.dresses.right], group: [s.dresses.men, s.dresses.women, s.dresses.children] }[s.people] || [s.dresses.single]);
const dressChanged = (s) => !!s && peopleDresses(s).some((d) => parseDress(d?.value).type !== 'original');
const bgChanged = (s) => !!s && s.bg !== KEEP_BG;
const studioOf = (s) => ((bgChanged(s) || dressChanged(s)) ? { people: s.people, bg: s.bg, bgBlend: s.bg !== KEEP_BG ? s.bgBlend : '', dresses: s.dresses } : null);
// Enhance first, then background, then dress (2026-11-03); the first one is the panel's opening tab.
const SECTIONS = [
    { key: 'tools', Icon: IconWand, labelKey: 'studioEditor.rail_tools', descKey: 'studioEditor.panelDesc_tools' },
    { key: 'bg', Icon: IconPhoto, labelKey: 'studioEditor.rail_bg', descKey: 'studioEditor.panelDesc_bg' },
    { key: 'dress', Icon: IconShirt, labelKey: 'studioEditor.rail_dress', descKey: 'studioEditor.panelDesc_dress' },
];


// Downscaled thumbnail for the film strip so HD (multi-MB) uploads don't jank the
// sidebar — the strip renders these small blobs, the canvas uses the full image.
const makeThumb = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const max = 320;
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        c.toBlob((b) => resolve(b ? URL.createObjectURL(b) : null), 'image/jpeg', 0.8);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

// Below `xl` a sidebar is a card floating out of the 56px rail
const RAIL_PANEL_CLS = 'max-xl:absolute max-xl:left-16 max-xl:top-2 max-xl:z-40 max-xl:max-h-[calc(100%-1rem)] max-xl:w-[min(360px,calc(100vw-4.5rem))] max-xl:overflow-hidden max-xl:rounded-2xl max-xl:border max-xl:shadow-2xl';

export default function BulkPhotoEditCreate() {
    const navigate = useNavigate();
    const buyCreditPath = useBuyCreditPath();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId'); // append mode
    const { t, currentLanguage } = useTranslation();
    const { user, getCurrentUser } = useAuthStore();
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const isBn = currentLanguage === 'bn';
    const fmtNum = (n) => (isBn ? String(n ?? 0).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : String(n ?? 0));

    const [projectName, setProjectName] = useState('');
    const [projectCount, setProjectCount] = useState(null); // # of the user's existing projects → name counter
    const nameTouched = useRef(false); // stop auto-filling once the user edits the name
    const [photos, setPhotos] = useState([]); // { file, url }
    const [perTheme, setPerTheme] = useState([]); // theme id per photo
    // Left-column tabs (desktop): 'upload' = the film strip, 'booth' = the photo-booth
    // queue. The booth tab exists only while the booth is ON for this office space.
    const [sideTab, setSideTab] = useState('upload');
    const [boothOn, setBoothOn] = useState(false);
    // Which booth photo became which batch File — so returning to the booth tab shows
    // already-added photos as checked. Pruned against the live batch, so deleting a
    // photo from the strip un-marks its booth tile.
    const boothFileIds = useRef(new Map()); // File -> booth upload id
    const addedBoothIds = (() => {
        const inBatch = new Set(photos.map(p => p.file));
        return [...boothFileIds.current.entries()].filter(([f]) => inBatch.has(f)).map(([, id]) => id);
    })();
    const [selected, setSelected] = useState(() => new Set()); // set of selected photo.url
    const [activeIdx, setActiveIdx] = useState(0);
    const [themes, setThemes] = useState([]);          // tools (sub-categories)
    const [categories, setCategories] = useState([]);  // main categories (Tab 1)
    const [inspectorTab, setInspectorTab] = useState(SECTIONS[0].key); // the panel's section: 'tools' | 'bg' | 'dress'
    const [pending, setPending] = useState(EMPTY_SETTINGS); // the panel's settings, applied to the selected photos
    const [perLevels, setPerLevels] = useState([]); // per-photo { toolId: level } (parallel to perTheme)
    const [perStudio, setPerStudio] = useState([]); // per-photo { people, bg, bgBlend, dresses } or null (parallel to perTheme)
    const { showSubColors } = useStudioDressOptions();
    const [creditPerImage, setCreditPerImage] = useState(0); // batch rate
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmRestyle, setConfirmRestyle] = useState(null); // { idx, count } when re-styling already-styled photos
    const [box, setBox] = useState({ w: 0, h: 0 }); // available canvas area (px) — caps the image
    const [natural, setNatural] = useState({ w: 0, h: 0 }); // active image's intrinsic size → scale it up to fill the canvas
    const mainRef = useRef(null);
    const fileRef = useRef(null);

    // Pin the editor to a fixed, non-scrolling panel that fills the viewport
    // below the app header. Header height varies by breakpoint / contest banner,
    // so measure it instead of hardcoding an offset.
    const [headerH, setHeaderH] = useState(0);
    const [isDesktop, setIsDesktop] = useState(true); // ≥1024px; below it the phone bottom nav takes the last 52px
    useLayoutEffect(() => {
        const header = document.querySelector('header.main-nav-wrapper');
        const measure = () => {
            const h = header ? Math.round(header.getBoundingClientRect().height) : 0;
            setHeaderH((prev) => (prev === h ? prev : h)); // guard: skip redundant re-renders
            setIsDesktop(window.innerWidth >= 1024);
        };
        measure();
        window.addEventListener('resize', measure);
        // The ContestBanner loads async and grows the header after mount. Re-measure
        // at a few settle points (not continuously — that jittered the layout) so the
        // fixed editor lands below the final header height.
        const timers = [80, 250, 600, 1200].map((ms) => setTimeout(measure, ms));
        return () => {
            window.removeEventListener('resize', measure);
            timers.forEach(clearTimeout);
        };
    }, []);

    // The editor is a full-screen fixed overlay — lock the page scroll so the
    // underlying scrollbar doesn't leave a gap on the right.
    useEffect(() => {
        const html = document.documentElement;
        const prevHtml = html.style.overflow;
        const prevBody = document.body.style.overflow;
        html.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return () => { html.style.overflow = prevHtml; document.body.style.overflow = prevBody; };
    }, []);

    // Below `xl` both sidebars shrink to an icon rail (as in the Studio Editor): a tap
    // floats that item's panel out beside the rail. 'strip' = film strip / booth, 'style' = a category's tools.
    const isCompact = useMediaQuery('(max-width: 1279px)');
    const [railPanel, setRailPanel] = useState('strip');
    const [panelOpen, setPanelOpen] = useState(false);
    const floatingOpen = isCompact && panelOpen;
    useEffect(() => { if (!isCompact) setPanelOpen(false); }, [isCompact]);
    useEffect(() => {
        if (!floatingOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPanelOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [floatingOpen]);

    // Measure the canvas area (inside its padding) so the image can be scaled to
    // fill it. A ResizeObserver is essential here: the editor is a FIXED overlay
    // whose height depends on `headerH`, which settles asynchronously (contest
    // banner) — a one-shot measure would capture a 0/tiny height and never update,
    // scaling the preview to nothing (the "blank preview" bug). The observer keeps
    // `box` correct through every layout change.
    useLayoutEffect(() => {
        const el = mainRef.current;
        if (!el) return;
        const measure = () => {
            const cs = getComputedStyle(el);
            const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
            const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
            setBox((prev) => {
                const w = Math.max(0, el.clientWidth - padX);
                const h = Math.max(0, el.clientHeight - padY);
                return prev.w === w && prev.h === h ? prev : { w, h };
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, []);

    // How many projects the user already has → the next project's serial number.
    useEffect(() => {
        if (projectId) return; // append mode keeps the existing name
        bulkPhotoService.list()
            .then((r) => setProjectCount(Array.isArray(r?.data) ? r.data.length : 0))
            .catch(() => setProjectCount(0));
    }, [projectId]);

    // Prefill the project name: "<shop name> - <today> - <n>" (falls back to
    // "Sohozkaj"), where <n> continues from the previous project. Matches the
    // generated-image naming pattern. Stops once the user edits the field.
    useEffect(() => {
        if (projectId || nameTouched.current || projectCount === null) return;
        const business = (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || '').trim() || 'Sohozkaj';
        const safeBusiness = business.replace(/[\\/:*?"<>|]/g, '-').trim();
        const today = new Date().toISOString().slice(0, 10);
        setProjectName(`${safeBusiness} - ${today} - ${projectCount + 1}`);
    }, [user, projectId, projectCount]);

    useEffect(() => {
        bulkPhotoService.getThemes().then((r) => {
            const cats = r.data?.categories || [];
            setThemes(r.data?.themes || []);
            setCategories(cats);
            // Batch is the only mode now — bill at the (discounted) batch rate.
            setCreditPerImage(r.data?.creditPerImageBatch ?? r.data?.creditPerImage ?? 0);
        }).catch(() => {});
    }, []);

    // Revoke object URLs on unmount ONLY. Removing/clearing already revokes the
    // dropped photos; a `[photos]`-scoped cleanup would revoke URLs still in use
    // by surviving photos (they share the same blob string) and break every image.
    const photosRef = useRef(photos);
    useEffect(() => { photosRef.current = photos; }, [photos]);
    useEffect(() => () => photosRef.current.forEach((p) => { URL.revokeObjectURL(p.url); if (p.thumb && p.thumb !== p.url) URL.revokeObjectURL(p.thumb); }), []);

    // Clear the cached intrinsic size when the previewed photo changes.
    useEffect(() => { setNatural({ w: 0, h: 0 }); }, [activeIdx]);

    // Reflect the CHECKBOX-SELECTED images' applied tools in the option cards: an
    // option shows as selected only when a selected image actually carries it.
    // Nothing selected → nothing checked. Multiple selected → union of their tools.
    // Re-runs only on selection change, so the user's in-progress toggles persist.
    // Tools ticked before any photo was selected survive the first selection: the
    // apply hint sends the user to the photos (another panel on small screens) to pick them.
    // The background / dress shown are the first selected photo's that has any.
    const hadSelection = useRef(false);
    useEffect(() => {
        const tools = {};
        let studio = null;
        photos.forEach((p, i) => {
            if (!selected.has(p.url)) return;
            (perTheme[i] || []).forEach((id) => { if (!(id in tools)) tools[id] = perLevels[i]?.[id] || null; });
            if (!studio && perStudio[i]) studio = perStudio[i];
        });
        const firstPick = !hadSelection.current;
        hadSelection.current = selected.size > 0;
        if (firstPick && !Object.keys(tools).length && !studio) return;
        setPending({ ...EMPTY_SETTINGS, ...(studio || {}), tools });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const styleName = (th) => (th ? (isBn ? th.nameBn || th.name : th.name) : '');
    const count = photos.length;
    // Photos that will actually be edited — oversized (`invalid`) tiles are excluded
    // from cost, validation and the batch payload, but still occupy a strip slot.
    const isValidPhoto = (p) => !p?.invalid;
    const validCount = photos.filter(isValidPhoto).length;
    // Tools applied to the ACTIVE photo (array of ids) + display helpers.
    const activeTools = count > 0 ? (perTheme[activeIdx] || []) : [];
    const toolById = (id) => themes.find((th) => th.id === id) || null;
    const toolsTitle = (ids) => (ids || []).map((id) => styleName(toolById(id))).filter(Boolean).join(', ');
    const activeAccent = activeTools.length ? swatchColor(toolById(activeTools[0])) : '#E68A00';
    const catName = (c) => (c ? (isBn ? c.nameBn || c.name : c.name) : '');
    // Style (= category) name for a set of applied tools; "Multiple" if they span categories.
    const styleNameForTools = (ids) => {
        const cats = [...new Set((ids || []).map((id) => toolById(id)?.category).filter(Boolean))];
        if (cats.length === 1) return catName(categories.find((c) => c.id === cats[0]));
        if (cats.length > 1) return isBn ? 'একাধিক স্টাইল' : 'Multiple styles';
        return '';
    };
    const levelLabel = (lvl) => (lvl ? t(`bulkPhotoEdit.create.level.${lvl}`) : '');
    // How many things a photo carries: its tools, plus its background and its dress when changed.
    const settingsCount = (i) => (perTheme[i]?.length || 0) + (bgChanged(perStudio[i]) ? 1 : 0) + (dressChanged(perStudio[i]) ? 1 : 0);
    const pendingToolIds = Object.keys(pending.tools);
    const pendingCount = pendingToolIds.length + (bgChanged(pending) ? 1 : 0) + (dressChanged(pending) ? 1 : 0);
    const perImageCost = creditPerImage;
    const totalCost = validCount * perImageCost;
    // Spendable balance = ALL pools (legacy + paid wallet + bonus), the same total the
    // header shows. `user.credit` alone is 0 for migrated users, so don't read it directly.
    const { legacy: legacyCredit, wallet: walletCredit } = creditParts(user);
    const availableCredit = legacyCredit + walletCredit;
    const insufficientCredit = validCount > 0 && totalCost > availableCredit;
    // Every VALID photo must carry at least one tool before the batch can run;
    // oversized tiles don't count toward the "unstyled" gate.
    const unstyledCount = photos.filter((p, i) => isValidPhoto(p) && settingsCount(i) === 0).length;
    const allStyled = validCount > 0 && unstyledCount === 0;
    const canStart = validCount >= MIN_IMAGES && allStyled;
    const estSeconds = validCount * SECONDS_PER_IMAGE;
    // Shown as a range: best case (per-image average) up to the worst-case
    // batch queue ceiling, so users aren't surprised by a slow batch.
    const estLabel = validCount === 0 ? ''
        : t('bulkPhotoEdit.create.estRangeValue', {
            from: estSeconds < 60
                ? t('bulkPhotoEdit.create.estSecondsValue', { n: fmtNum(estSeconds) })
                : t('bulkPhotoEdit.create.estMinutesValue', { n: fmtNum(Math.ceil(estSeconds / 60)) }),
            hours: fmtNum(MAX_BATCH_HOURS),
        });

    const addFiles = useCallback((fileList) => {
        const imgs = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
        // Reject formats the batch pipeline can't handle: SVG, AVIF, GIF.
        const formatOk = imgs.filter((f) => !(/(svg|avif|gif)/i.test(f.type) || /\.(svg|avif|gif)$/i.test(f.name)));
        if (formatOk.length < imgs.length) {
            notifications.show({ message: t('bulkPhotoEdit.create.unsupportedFormat'), color: 'red' });
        }
        // Files over the size limit are NOT rejected — they're added but flagged
        // `invalid` so they show in the strip with an error badge, can't be selected,
        // and are dropped from the batch at submit time.
        const incoming = formatOk;
        if (!incoming.length) return;
        // Skip images already in the queue (and duplicates within this batch) — same
        // file can't be added twice. Matched by name + size.
        const existing = new Set(photos.map((p) => `${p.file.name}:${p.file.size}`));
        const seen = new Set();
        const unique = incoming.filter((f) => {
            const key = `${f.name}:${f.size}`;
            if (existing.has(key) || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        if (incoming.length > unique.length) {
            notifications.show({ message: t('bulkPhotoEdit.create.duplicateSkipped'), color: 'orange' });
        }
        if (!unique.length) return;
        // No theme by default — the user picks images then applies a style. `thumb`
        // starts as the full URL and is swapped for a small downscaled blob once ready.
        // Oversized files carry `invalid: 'tooLarge'` so the tile renders as failed.
        setPhotos((prev) => [...prev, ...unique.map((file) => { const url = URL.createObjectURL(file); return { file, url, thumb: url, invalid: file.size > MAX_FILE_BYTES ? 'tooLarge' : null }; })]);
        setPerTheme((prev) => [...prev, ...unique.map(() => [])]);
        setPerLevels((prev) => [...prev, ...unique.map(() => ({}))]);
        setPerStudio((prev) => [...prev, ...unique.map(() => null)]);
        // Build lightweight thumbnails off the main thread-ish (async decode) so the
        // film strip stays smooth with HD images; swap each in when ready.
        unique.forEach(async (file) => {
            const thumb = await makeThumb(file);
            if (thumb) setPhotos((prev) => prev.map((p) => (p.file === file ? { ...p, thumb } : p)));
        });
    }, [photos, t]);

    // Revoke both the full and thumbnail blob URLs a photo holds.
    const revokePhoto = (p) => { if (p?.url) URL.revokeObjectURL(p.url); if (p?.thumb && p.thumb !== p.url) URL.revokeObjectURL(p.thumb); };

    const removePhoto = (idx, e) => {
        e?.stopPropagation();
        const url = photos[idx]?.url;
        revokePhoto(photos[idx]);
        setPhotos((prev) => prev.filter((_, i) => i !== idx));
        setPerTheme((prev) => prev.filter((_, i) => i !== idx));
        setPerLevels((prev) => prev.filter((_, i) => i !== idx));
        setPerStudio((prev) => prev.filter((_, i) => i !== idx));
        setSelected((prev) => { if (!prev.has(url)) return prev; const n = new Set(prev); n.delete(url); return n; });
        setActiveIdx((cur) => Math.max(0, idx < cur ? cur - 1 : Math.min(cur, count - 2)));
    };
    const clearAll = () => { photos.forEach(revokePhoto); setPhotos([]); setPerTheme([]); setPerLevels([]); setPerStudio([]); setSelected(new Set()); setActiveIdx(0); };

    // Selection: toggle one, toggle all, and delete the selected images at once.
    const toggleSelect = (url) => setSelected((prev) => { const n = new Set(prev); n.has(url) ? n.delete(url) : n.add(url); return n; });
    const allSelected = validCount > 0 && selected.size === validCount;
    const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(photos.filter(isValidPhoto).map((p) => p.url)));
    const removeSelected = () => {
        if (!selected.size) return;
        const keep = (_, i) => !selected.has(photos[i].url);
        photos.forEach((p) => selected.has(p.url) && revokePhoto(p));
        const removedBefore = photos.slice(0, activeIdx + 1).filter((p) => selected.has(p.url)).length;
        setPhotos((prev) => prev.filter(keep));
        setPerTheme((prev) => prev.filter(keep));
        setPerLevels((prev) => prev.filter(keep));
        setPerStudio((prev) => prev.filter(keep));
        setActiveIdx((cur) => Math.max(0, Math.min(cur - removedBefore, count - selected.size - 1)));
        setSelected(new Set());
    };
    const step = (dir) => count && setActiveIdx((cur) => (cur + dir + count) % count);

    // ── Selection interactions ─────────────────────────────────────────────
    // Click a tile: preview it and toggle its selection at the same time.
    // Shift+click: range-select from the anchor to the clicked tile.
    // Ctrl/⌘+click: toggle a single tile without clearing the rest.
    const lastIndexRef = useRef(null);      // anchor for shift-range selection
    const suppressClickRef = useRef(false); // set after a marquee drag so the trailing click is ignored

    const selectRange = (from, to) => {
        const [a, b] = from <= to ? [from, to] : [to, from];
        const range = photos.slice(a, b + 1).filter(isValidPhoto).map((p) => p.url);
        setSelected((prev) => new Set([...prev, ...range]));
    };

    const onTileClick = (i, e) => {
        if (suppressClickRef.current) return; // just finished a marquee drag
        if (photos[i]?.invalid) return;       // oversized tiles can't be previewed or selected
        if (e.shiftKey) {
            selectRange(lastIndexRef.current ?? i, i);
            setActiveIdx(i);
            return;
        }
        if (e.ctrlKey || e.metaKey) {
            toggleSelect(photos[i].url);
            lastIndexRef.current = i;
            return;
        }
        // Plain click: preview the tile AND toggle its selection in one action.
        setActiveIdx(i);
        toggleSelect(photos[i].url);
        lastIndexRef.current = i;
    };

    // Checkbox click: shift extends the range, otherwise toggle just this tile.
    const onCheckboxClick = (i, e) => {
        e.stopPropagation();
        if (photos[i]?.invalid) return; // oversized tiles can't be selected
        if (e.shiftKey && lastIndexRef.current !== null) { selectRange(lastIndexRef.current, i); return; }
        toggleSelect(photos[i].url);
        lastIndexRef.current = i;
    };

    // ── Drag-to-select (marquee) — desktop only ────────────────────────────
    // Dragging a rectangle over the grid selects the tiles it touches. Holding
    // Shift/Ctrl/⌘ adds to the existing selection instead of replacing it. Touch
    // skips it, so a finger just scrolls the strip.
    const stripRef = useRef(null);
    const marqueeRef = useRef(null);        // { startX, startY, additive, base:Set, moved, pointerId }
    const [marqueeBox, setMarqueeBox] = useState(null); // client-coord rect for the overlay

    const gridPointerDown = (e) => {
        if (e.pointerType === 'touch' || e.button !== 0 || e.target.closest('[data-no-marquee]')) return;
        marqueeRef.current = {
            startX: e.clientX, startY: e.clientY,
            additive: e.shiftKey || e.ctrlKey || e.metaKey,
            base: new Set(selected),
            moved: false, pointerId: e.pointerId,
        };
    };
    const gridPointerMove = (e) => {
        const m = marqueeRef.current;
        if (!m) return;
        if (!m.moved && Math.hypot(e.clientX - m.startX, e.clientY - m.startY) < 5) return;
        if (!m.moved) { m.moved = true; try { e.currentTarget.setPointerCapture(m.pointerId); } catch { /* ignore */ } }
        e.preventDefault();
        const box = {
            left: Math.min(m.startX, e.clientX), top: Math.min(m.startY, e.clientY),
            right: Math.max(m.startX, e.clientX), bottom: Math.max(m.startY, e.clientY),
        };
        setMarqueeBox(box);
        const hits = new Set();
        (stripRef.current?.querySelectorAll('[data-idx]') || []).forEach((el) => {
            const r = el.getBoundingClientRect();
            if (!(r.right < box.left || r.left > box.right || r.bottom < box.top || r.top > box.bottom)) {
                const p = photos[Number(el.dataset.idx)];
                if (p && isValidPhoto(p)) hits.add(p.url);
            }
        });
        setSelected(m.additive ? new Set([...m.base, ...hits]) : hits);
    };
    const gridPointerUp = (e) => {
        const m = marqueeRef.current;
        if (m?.moved) { suppressClickRef.current = true; setTimeout(() => { suppressClickRef.current = false; }, 0); }
        try { if (m?.pointerId != null) e.currentTarget.releasePointerCapture(m.pointerId); } catch { /* ignore */ }
        marqueeRef.current = null;
        setMarqueeBox(null);
    };

    // Write the given tools + levels and the background / dress settings onto every checkbox-selected photo, then clear.
    const writeTools = (tools, levels, studio) => {
        setPerTheme((prev) => prev.map((v, i) => (selected.has(photos[i].url) ? tools : v)));
        setPerLevels((prev) => prev.map((v, i) => (selected.has(photos[i].url) ? levels : v)));
        setPerStudio((prev) => prev.map((v, i) => (selected.has(photos[i].url) ? studio : v)));
        setSelected(new Set());
    };

    // "Apply" → puts the panel's settings (tools with their levels, background / merge, dress) on the selected photos.
    // Requires a photo selection first; if some already carry settings, confirm first.
    const applyTools = () => {
        if (!selected.size) {
            notifications.show({ message: t('bulkPhotoEdit.create.selectImagesFirst'), color: 'orange' });
            return;
        }
        const tools = pendingToolIds;
        const levels = Object.fromEntries(Object.entries(pending.tools).filter(([, lvl]) => lvl));
        const studio = studioOf(pending);
        const already = photos.filter((p, i) => selected.has(p.url) && settingsCount(i) > 0).length;
        if (already > 0) {
            setConfirmRestyle({ tools, levels, studio, count: already });
            return;
        }
        writeTools(tools, levels, studio);
    };

    const activePhoto = photos[Math.min(activeIdx, count - 1)];

    // Scale the active image to FILL the canvas area (upscaling small images too),
    // preserving aspect ratio. Needs a REAL canvas size (box) and the image's
    // intrinsic size (from onLoad/ref). The `> 40` guard means a not-yet-measured
    // or bogus tiny box falls back to natural display instead of a ~1px blank.
    const fit = (box.w > 40 && box.h > 40 && natural.w && natural.h)
        ? (() => {
            const s = Math.min(box.w / natural.w, box.h / natural.h); // may be >1 → enlarge
            return { w: Math.round(natural.w * s), h: Math.round(natural.h * s) };
        })()
        : null;

    // ── Keyboard shortcuts ──────────────────────────────────────────────────
    // Ctrl/Cmd+U upload · Ctrl/Cmd+A select-all · 1–9 apply preset N · Ctrl/Cmd+Enter start.
    // Ignored while typing in a field or when any dialog is open.
    useEffect(() => {
        const onKey = (e) => {
            if (confirmOpen || confirmRestyle) return;
            const el = e.target;
            const typing =
                /^(input|textarea|select)$/.test((el?.tagName || '').toLowerCase()) || el?.isContentEditable;
            if (typing) return;
            const mod = e.ctrlKey || e.metaKey;

            // Upload images — Ctrl/Cmd + U
            if (mod && !e.shiftKey && !e.altKey && (e.key === 'u' || e.key === 'U')) {
                e.preventDefault();
                fileRef.current?.click();
                return;
            }
            // Select / deselect all — Ctrl/Cmd + A
            if (mod && !e.shiftKey && !e.altKey && (e.key === 'a' || e.key === 'A')) {
                if (count > 0) { e.preventDefault(); toggleSelectAll(); }
                return;
            }
            // Start generation (open confirm) — Ctrl/Cmd + Enter or Ctrl/Cmd + G
            if ((mod && e.key === 'Enter') || (mod && !e.shiftKey && !e.altKey && (e.key === 'g' || e.key === 'G'))) {
                e.preventDefault();
                if (canStart && !submitting) setConfirmOpen(true);
                return;
            }
            // Browse the center preview like a gallery — ← / →
            if (!mod && !e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                if (count > 1) { e.preventDefault(); step(e.key === 'ArrowLeft' ? -1 : 1); }
                return;
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [count, themes, submitting, selected, photos, perTheme, confirmOpen, confirmRestyle]);

    // Paste-to-upload: add images copied to the clipboard (Cmd/Ctrl+V) to the queue.
    useEffect(() => {
        const onPaste = (e) => {
            if (confirmOpen || confirmRestyle) return;
            const el = e.target;
            if (/^(input|textarea|select)$/.test((el?.tagName || '').toLowerCase()) || el?.isContentEditable) return;
            const files = Array.from(e.clipboardData?.items || [])
                .filter((i) => i.type.startsWith('image/'))
                .map((i) => i.getAsFile())
                .filter(Boolean);
            if (!files.length) return;
            e.preventDefault();
            addFiles(files);
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [addFiles, confirmOpen, confirmRestyle]);

    const doCreate = async () => {
        if (!validCount) { notifications.show({ message: t('bulkPhotoEdit.create.noFiles'), color: 'orange' }); return; }
        // Close the confirm modal right away so the user isn't stuck watching a
        // spinner inside it — the upload continues and we redirect to the project
        // processing page as soon as the server returns the new id.
        setConfirmOpen(false);
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('name', projectName || (isBn ? 'নতুন বাল্ক এডিট' : 'New Bulk Edit'));
            fd.append('mode', 'batch');
            // Only VALID photos go to the server — oversized tiles are dropped here so
            // the files and their per-photo tools stay index-aligned.
            const validEntries = photos.map((p, i) => ({ p, i })).filter(({ p }) => isValidPhoto(p));
            const validPhotos = validEntries.map(({ p }) => p);
            // Each photo → array of entries, "id" or "id:level" for leveled tools.
            // Backend maps these to the command tokens. Batch default = first photo
            // that has any tools.
            const themesPayload = validEntries.map(({ i }) => (perTheme[i] || []).map((id) => {
                const lvl = perLevels[i]?.[id];
                return lvl ? `${id}:${lvl}` : id;
            }));
            const defaultTheme = (themesPayload.find((a) => a && a.length) || []).join(',');
            if (defaultTheme) fd.append('theme', defaultTheme);
            fd.append('themes', JSON.stringify(themesPayload));
            // Each photo's Studio-style settings (every photo gets the studio prompt; untouched ones keep their own background and dress).
            const settingsPayload = validEntries.map(({ i }) => {
                const st = perStudio[i] || EMPTY_SETTINGS;
                return {
                    people: st.people,
                    bg: isValidBg(st.bg) ? st.bg : KEEP_BG,
                    bgBlend: st.bg !== KEEP_BG && BG_BLENDS.includes(st.bgBlend) ? st.bgBlend : '',
                    ...studioDressRequest(st.people, st.dresses, showSubColors),
                };
            });
            fd.append('settings', JSON.stringify(settingsPayload));
            if (projectId) fd.append('projectId', projectId);
            // Tag the project with the active office space so the list can scope it
            // (mirrors how generated docs/images are attributed to a space).
            if (selectedOfficeSpace) fd.append('officeSpaceId', selectedOfficeSpace.id);
            validPhotos.forEach((p) => fd.append('files', p.file, p.file.name));

            const res = await bulkPhotoService.create(fd);
            const newId = res.data?.id || projectId;
            getCurrentUser().catch(() => {}); // the photos are paid for up front: refresh the shown balance
            notifications.show({ message: res.message || t('common.success'), color: 'green' });
            // Credit ran out after the pre-check (spent elsewhere meanwhile): only part of the batch was queued.
            if (res.data?.creditShortfall > 0) notifications.show({ message: t('bulkPhotoEdit.create.partialCredits', { n: res.data.creditShortfall }), color: 'orange' });
            navigate(`/bulk-photo-edit/${newId}`);
        } catch (e) {
            const msg = e.code === 'INSUFFICIENT_CREDITS' ? t('bulkPhotoEdit.create.insufficientCredits') : (e.message || t('bulkPhotoEdit.create.createError'));
            notifications.show({ message: msg, color: 'red' });
        } finally { setSubmitting(false); }
    };

    // Rail items: the photos (+ booth while it's ON), then one per style category.
    const photoRail = [
        { key: 'upload', Icon: IconPhoto, label: t('bulkPhotoEdit.create.railPhotos'), count, select: () => { setRailPanel('strip'); setSideTab('upload'); } },
        ...(boothOn ? [{ key: 'booth', Icon: IconDeviceMobileMessage, label: t('photoCollectorPanel.title'), select: () => { setRailPanel('strip'); setSideTab('booth'); } }] : []),
    ];
    const sectionCount = { bg: bgChanged(pending) ? 1 : 0, dress: dressChanged(pending) ? 1 : 0, tools: pendingToolIds.length };
    const styleRail = SECTIONS.map((sec) => ({
        key: sec.key,
        Icon: sec.Icon,
        label: t(sec.labelKey),
        count: sectionCount[sec.key],
        select: () => { setRailPanel('style'); setInspectorTab(sec.key); },
    }));
    const railKey = railPanel === 'strip' ? (boothOn ? sideTab : 'upload') : inspectorTab;
    const activeSection = SECTIONS.find((sec) => sec.key === inspectorTab) || SECTIONS[0];
    const pickRail = (item) => {
        if (floatingOpen && item.key === railKey) { setPanelOpen(false); return; }
        item.select();
        setPanelOpen(true);
    };
    const railBtn = (item) => {
        const active = item.key === railKey;
        return (
            <Tooltip key={item.key}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={() => pickRail(item)}
                        aria-label={item.label}
                        aria-expanded={floatingOpen && active}
                        className={`group relative flex w-full flex-shrink-0 items-center justify-center rounded-xl py-1 transition-colors ${active ? 'text-primary' : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-100'}`}
                    >
                        {active && <span className="absolute -left-1.5 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-full bg-primary" />}
                        <span className={`relative flex h-9 w-9 items-center justify-center rounded-xl transition-all ${active ? 'bg-primary text-white shadow-md shadow-primary/30' : 'bg-white ring-1 ring-zinc-200 group-hover:ring-zinc-300 dark:bg-zinc-800 dark:ring-zinc-700'}`}>
                            <item.Icon size={18} stroke={1.8} />
                            {item.count > 0 && (
                                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-zinc-900">{fmtNum(item.count)}</span>
                            )}
                        </span>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="z-[2000]">{item.label}</TooltipContent>
            </Tooltip>
        );
    };
    const panelCloseBtn = (
        <button type="button" onClick={() => setPanelOpen(false)} aria-label={t('common.close')} className="flex-shrink-0 rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 xl:hidden">
            <IconX size={17} />
        </button>
    );
    const panelCls = (key) => (floatingOpen && railPanel === key ? 'flex max-xl:animate-in max-xl:fade-in-0 max-xl:slide-in-from-left-2 max-xl:duration-200' : 'hidden xl:flex');

    // Footer — submit. Batch needs at least MIN_IMAGES photos. Sits under the inspector
    // on desktop; below `xl` it docks under the preview so it's always in reach.
    const submitFooter = (
        <div className={`flex-shrink-0 border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 ${isCompact ? 'col-start-2 row-start-2 p-2' : 'p-4'}`}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={() => setConfirmOpen(true)}
                        disabled={!canStart || submitting}
                        className="w-full h-11 xl:h-12 rounded-xl bg-gradient-to-br from-primary to-[#c77400] text-white text-[14.5px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-primary/30 hover:brightness-105 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {submitting ? <IconLoader2 size={17} className="animate-spin" /> : <IconSparkles size={17} stroke={1.9} />} {t('bulkPhotoEdit.create.enhanceBtn', { count: fmtNum(validCount) })}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top">{validCount < MIN_IMAGES ? t('bulkPhotoEdit.create.minImagesHint', { n: fmtNum(MIN_IMAGES) }) : !allStyled ? t('bulkPhotoEdit.create.applyToAllHint', { n: fmtNum(unstyledCount) }) : `${t('bulkPhotoEdit.create.tipEnhance')} (Ctrl+G)`}</TooltipContent>
            </Tooltip>
            {validCount > 0 && validCount < MIN_IMAGES ? (
                <p className="mt-2 text-center text-[11.5px] font-medium text-amber-600 dark:text-amber-400">
                    {t('bulkPhotoEdit.create.minImagesHint', { n: fmtNum(MIN_IMAGES) })}
                </p>
            ) : validCount >= MIN_IMAGES && !allStyled ? (
                <p className="mt-2 text-center text-[11.5px] font-medium text-amber-600 dark:text-amber-400">
                    {t('bulkPhotoEdit.create.applyToAllHint', { n: fmtNum(unstyledCount) })}
                </p>
            ) : null}
        </div>
    );

    return (
        <div
            style={{ top: headerH, height: `calc(100dvh - ${headerH + (isDesktop ? 0 : 52)}px)` }}
            className="font-hind flex flex-col bg-[var(--color-page)] fixed left-0 right-0 z-30 overflow-hidden"
        >
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

            {/* Drag-to-select rubber band (desktop) */}
            {marqueeBox && (
                <div
                    className="fixed z-[60] rounded-sm border border-primary bg-primary/15 pointer-events-none"
                    style={{ left: marqueeBox.left, top: marqueeBox.top, width: marqueeBox.right - marqueeBox.left, height: marqueeBox.bottom - marqueeBox.top }}
                />
            )}

            {/* ── Editor grid: film strip (left col), canvas (center), inspector (right col).
                Below `xl`: the rail, then the canvas over the submit bar; the two sidebars float out of the rail. ── */}
            <div className="relative flex-1 min-h-0 grid grid-cols-[56px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] xl:grid-cols-[300px_minmax(0,1fr)_400px] xl:grid-rows-1 overflow-hidden bg-white dark:bg-zinc-900">

                {isCompact && (
                    <nav aria-label={t('bulkPhotoEdit.create.stylePresets')} className="relative z-40 col-start-1 row-span-2 row-start-1 flex min-h-0 flex-col gap-1 overflow-y-auto bg-zinc-100 p-1.5 dark:bg-zinc-950">
                        {photoRail.map(railBtn)}
                        <span className="mx-auto my-1 h-px w-6 flex-shrink-0 bg-zinc-300 dark:bg-zinc-700" />
                        {styleRail.map(railBtn)}
                    </nav>
                )}
                {floatingOpen && <div className="absolute inset-0 z-30 bg-black/20 animate-in fade-in-0 duration-200" onClick={() => setPanelOpen(false)} />}

                {/* Canvas */}
                <main ref={mainRef} className="relative col-start-2 row-start-1 flex items-center justify-center p-2 overflow-hidden sk-editor-stage min-h-0">

                    {count > 1 && (
                        <>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button onClick={() => step(-1)} aria-label={t('bulkPhotoEdit.create.tipPrev')} className="absolute left-4 top-1/2 -translate-y-1/2 z-[4] w-[42px] h-[42px] rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/90 backdrop-blur text-zinc-700 dark:text-zinc-200 flex items-center justify-center shadow-sm hover:bg-white dark:hover:bg-zinc-800 transition-colors">
                                        <IconChevronLeft size={18} stroke={2} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">{t('bulkPhotoEdit.create.tipPrev')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button onClick={() => step(1)} aria-label={t('bulkPhotoEdit.create.tipNext')} className="absolute right-4 top-1/2 -translate-y-1/2 z-[4] w-[42px] h-[42px] rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/90 backdrop-blur text-zinc-700 dark:text-zinc-200 flex items-center justify-center shadow-sm hover:bg-white dark:hover:bg-zinc-800 transition-colors">
                                        <IconChevronRight size={18} stroke={2} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">{t('bulkPhotoEdit.create.tipNext')}</TooltipContent>
                            </Tooltip>
                        </>
                    )}

                    {/* Canvas — the bordered card shrink-wraps the image, so it fits
                        the image's own orientation (landscape or portrait), uncropped. */}
                    <div
                        style={{
                            ...(activePhoto && activeTools.length ? { borderColor: activeAccent } : {}),
                            ...(activePhoto && fit ? { width: fit.w, height: fit.h } : {}),
                        }}
                        className={`group relative z-[2] rounded-2xl overflow-hidden shadow-lg select-none bg-white/5 ${activePhoto ? `border-[3px] border-white/20 max-w-full max-h-full ${fit ? '' : 'w-fit h-fit'}` : 'w-[min(90%,46vh)] max-w-[350px] aspect-[3/4] border-2 border-dashed border-white/15 transition-colors hover:border-primary hover:bg-primary/5'}`}
                    >
                        {activePhoto ? (
                            <>
                                <img
                                    key={activeIdx}
                                    src={activePhoto.url}
                                    alt=""
                                    draggable="false"
                                    // Measure intrinsic size two ways so `fit` is reliable: onLoad
                                    // for fresh loads, and a ref that reads an ALREADY-decoded
                                    // (cached) image on mount — the cached case often skips onLoad,
                                    // which is what left the preview tiny/blank after switching.
                                    ref={(el) => { if (el?.complete && el.naturalWidth) setNatural((n) => (n.w === el.naturalWidth && n.h === el.naturalHeight ? n : { w: el.naturalWidth, h: el.naturalHeight })); }}
                                    onLoad={(e) => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                                    className={`block object-contain ${fit ? 'w-full h-full' : 'max-w-full max-h-full w-auto h-auto'}`}
                                    style={fit ? undefined : { maxWidth: box.w > 40 ? box.w : undefined, maxHeight: box.h > 40 ? box.h : undefined }}
                                />
                                {/* Applied style + options — a compact summary pill and small
                                    WRAPPED chips (multiple per row) so many tools stay readable and
                                    never grow into a tall column. Hidden while hovering the image. */}
                                {count > 0 && settingsCount(activeIdx) > 0 && (
                                    <div className="absolute top-3 left-3 right-3 z-[5] flex flex-col items-start gap-1.5 group-hover:opacity-0 transition-opacity">
                                        <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/95 dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700 shadow-sm text-[11px] font-semibold text-zinc-800 dark:text-zinc-100">
                                            <span className="w-[18px] h-[18px] rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><IconCheck size={11} stroke={3.2} className="text-white" /></span>
                                            {styleNameForTools(activeTools) || t('bulkPhotoEdit.create.stylePresets')}
                                            <span className="text-primary">· {t('bulkPhotoEdit.create.settingsCount', { n: fmtNum(settingsCount(activeIdx)) })}</span>
                                        </span>
                                        <div className="flex flex-col items-start gap-1">
                                            {/* the photo's background and dress, when changed */}
                                            {[bgChanged(perStudio[activeIdx]) && 'rail_bg', dressChanged(perStudio[activeIdx]) && 'rail_dress'].filter(Boolean).map((key) => (
                                                <span key={key} className="inline-flex items-center gap-1.5 h-6 pl-1 pr-2.5 rounded-full bg-white/95 dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700 shadow-sm text-[10.5px] font-medium text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                                                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><IconCheck size={10} stroke={3.2} className="text-white" /></span>
                                                    {t(`studioEditor.${key}`)}
                                                </span>
                                            ))}
                                            {activeTools.map((id, i) => {
                                                const lvl = perLevels[activeIdx]?.[id];
                                                return (
                                                    <span
                                                        key={id}
                                                        style={{ animationDelay: `${i * 180}ms` }}
                                                        className="bulk-chip-in inline-flex items-center gap-1.5 h-6 pl-1 pr-2.5 rounded-full bg-white/95 dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700 shadow-sm text-[10.5px] font-medium text-zinc-700 dark:text-zinc-200 whitespace-nowrap"
                                                    >
                                                        <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><IconCheck size={10} stroke={3.2} className="text-white" /></span>
                                                        {styleName(toolById(id))}
                                                        {lvl && <span className="text-primary font-semibold">· {levelLabel(lvl)}</span>}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {/* Counter — hidden while hovering the image */}
                                {count > 0 && (
                                    <span className="absolute bottom-3 left-3 z-[5] inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white/95 dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700 shadow-sm text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 group-hover:opacity-0 transition-opacity">
                                        <span className="text-zinc-900 dark:text-zinc-100">{fmtNum(activeIdx + 1)}</span> / {fmtNum(count)}
                                    </span>
                                )}
                            </>
                        ) : (
                            /* The photo editors' upload card: the size badge here says how many photos a batch needs */
                            <button type="button" onClick={() => fileRef.current?.click()} className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-6 text-center">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-white/60 transition-colors group-hover:bg-primary/15"><IconPhoto size={32} /></span>
                                <span className="text-[17px] font-bold leading-snug !text-white">{t('bulkPhotoEdit.create.uploadTitle')}</span>
                                <span className="text-[13px] leading-[1.7] !text-white/50">
                                    {boothOn ? t('imageGeneration.uploadHint') : t('bulkPhotoEdit.create.addHint')}
                                    <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 align-middle text-[11.5px] font-bold !text-white/75">{t('bulkPhotoEdit.create.minPhotosBadge', { n: fmtNum(MIN_IMAGES) })}</span>
                                </span>
                                <span className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold !text-white transition-colors group-hover:bg-primary/90"><IconUpload size={15} />{t('imageGeneration.uploadButton')}</span>
                                <kbd className="mt-1 rounded-[5px] border border-white/25 bg-white/10 px-1.5 py-0.5 text-[11px] font-semibold text-white/75">Ctrl+U</kbd>
                            </button>
                        )}
                    </div>

                </main>

                {/* Film strip — left sidebar on desktop, floating panel from the rail below `xl` */}
                <div className={`${RAIL_PANEL_CLS} ${panelCls('strip')} xl:col-start-1 xl:row-start-1 bg-white dark:bg-zinc-900 flex-col items-center min-w-0 min-h-0 border-zinc-200 dark:border-zinc-700 xl:border-r gap-3 p-3`}>
                    {isCompact && (
                        <div className="flex w-full flex-shrink-0 items-center gap-2">
                            <h2 className="min-w-0 flex-1 truncate text-[14px] font-bold text-zinc-900 dark:text-zinc-100">{boothOn && sideTab === 'booth' ? t('photoCollectorPanel.title') : t('bulkPhotoEdit.create.railPhotos')}</h2>
                            {panelCloseBtn}
                        </div>
                    )}
                    {/* Desktop tabs: আপলোড (film strip) / ফটো বুথ (customer queue). The booth
                        tab only exists while the booth is ON; a tap on a booth photo adds it
                        into the batch. The rail has its own booth item. */}
                    {boothOn && (
                        <div className="hidden xl:grid w-full grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                            {[
                                { id: 'upload', label: t('photoCollectorPanel.tabUpload') },
                                { id: 'booth', label: t('photoCollectorPanel.title') },
                            ].map((tb) => (
                                <button key={tb.id} type="button" onClick={() => setSideTab(tb.id)}
                                    className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${sideTab === tb.id ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                    {tb.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Booth pane — kept mounted (hidden) so the on/off status resolves and the
                        queue stays warm; visible only on its tab. Multi-select: taps CHOOSE
                        photos, then the সিলেক্ট button adds them all to the batch at once and
                        returns to the আপলোড tab where they show in the strip. Already-added
                        photos stay checked (markedIds), so the batch list isn't repeated here. */}
                    <div className={boothOn && sideTab === 'booth' ? 'w-full flex-1 min-h-0 overflow-y-auto' : 'hidden'}>
                        <PhotoBoothInbox
                            multiSelect
                            markedIds={addedBoothIds}
                            onPickFiles={(files, ids) => {
                                files.forEach((f, i) => boothFileIds.current.set(f, ids[i]));
                                addFiles(files);
                                setSideTab('upload');
                            }}
                            onStatusChange={setBoothOn}
                        />
                    </div>
                    <div
                        ref={stripRef}
                        onPointerDown={gridPointerDown}
                        onPointerMove={gridPointerMove}
                        onPointerUp={gridPointerUp}
                        className={`${boothOn && sideTab === 'booth' ? 'hidden' : 'grid'} flex-1 min-w-0 min-h-0 w-full grid-cols-3 xl:grid-cols-2 content-start auto-rows-max items-start gap-2.5 select-none -mr-3 pr-3 overflow-x-hidden overflow-y-auto cursor-crosshair`}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button data-no-marquee onClick={() => fileRef.current?.click()} className="flex-shrink-0 w-full aspect-[3/4] gap-1 border-[1.5px] border-dashed border-primary/40 bg-primary/5 rounded-2xl flex flex-col items-center justify-center text-primary hover:bg-primary/10 transition-colors">
                                    <IconPlus size={24} stroke={1.9} />
                                    <span className="text-[11px] font-semibold">{t('bulkPhotoEdit.create.addPhotos')}</span>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right">{`${t('bulkPhotoEdit.create.tipAddPhotos')} (Ctrl+U)`}</TooltipContent>
                        </Tooltip>
                        {photos.map((photo, i) => {
                            const invalid = !!photo.invalid;
                            const isSel = !invalid && selected.has(photo.url);
                            const photoTools = perTheme[i] || [];
                            const photoAccent = photoTools.length ? swatchColor(toolById(photoTools[0])) : '#E68A00';
                            return (
                            <button
                                key={photo.url}
                                data-idx={i}
                                // Click previews the tile AND toggles its selection;
                                // Shift+click range-selects, Ctrl/⌘+click toggles a single tile.
                                // Drag a rectangle over tiles to marquee-select.
                                // Oversized (invalid) tiles are inert — they only show the error + a remove button.
                                onClick={(e) => onTileClick(i, e)}
                                className={`group relative flex-shrink-0 w-full aspect-[3/4] rounded-xl overflow-hidden border-2 transition-colors ${invalid ? 'border-red-400 dark:border-red-500/70 cursor-not-allowed' : `cursor-pointer ${isSel ? 'border-primary ring-2 ring-primary/40' : i === activeIdx ? 'border-primary' : 'border-zinc-200 dark:border-zinc-700'}`}`}
                            >
                                <img src={photo.thumb || photo.url} alt="" loading="lazy" decoding="async" className={`w-full h-full object-cover ${invalid ? 'opacity-40 grayscale' : ''}`} draggable="false" />
                                {isSel && <span className="pointer-events-none absolute inset-0 bg-primary/25" />}
                                {invalid && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="pointer-events-none absolute inset-0 z-[2] flex flex-col items-center justify-center gap-0.5 bg-red-500/25">
                                                <IconAlertTriangle size={18} className="text-red-600 dark:text-red-400" stroke={2.2} />
                                                <span className="block text-[9px] font-bold text-red-700 dark:text-red-300 leading-tight text-center px-1">
                                                    {t('bulkPhotoEdit.create.tooLargeTag', { mb: fmtNum(MAX_FILE_MB) })}
                                                </span>
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">{t('bulkPhotoEdit.create.fileTooLarge', { mb: fmtNum(MAX_FILE_MB) })}</TooltipContent>
                                    </Tooltip>
                                )}
                                {/* Selection checkbox — hidden until hover; stays visible once selected. Not shown on invalid tiles. */}
                                {!invalid && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span
                                            data-no-marquee
                                            role="checkbox"
                                            aria-checked={isSel}
                                            tabIndex={0}
                                            onClick={(e) => onCheckboxClick(i, e)}
                                            className={`absolute top-1 left-1 z-[2] flex items-center justify-center w-[20px] h-[20px] rounded-md bg-white/85 dark:bg-zinc-900/80 shadow-sm cursor-pointer hover:bg-white dark:hover:bg-zinc-900 transition-opacity ${isSel ? 'opacity-100' : 'opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus:opacity-100'}`}
                                        >
                                            {isSel
                                                ? <IconSquareCheckFilled size={18} className="text-primary" />
                                                : <IconSquare size={16} className="text-zinc-400 dark:text-zinc-500" stroke={2} />}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right">{t(isSel ? 'bulkPhotoEdit.create.tipDeselect' : 'bulkPhotoEdit.create.tipSelect')}</TooltipContent>
                                </Tooltip>
                                )}
                                <span className="absolute bottom-1 left-1 text-[9px] font-bold text-white bg-black/45 px-1 rounded">{fmtNum(i + 1)}</span>
                                {/* Applied-tool COUNT — short & always readable on the tiny tile;
                                    full tool list is in the title tooltip. Hidden on hover. */}
                                {!invalid && settingsCount(i) > 0 && (
                                    <span
                                        style={{ backgroundColor: rgba(photoAccent, 0.92) }}
                                        title={[bgChanged(perStudio[i]) && t('studioEditor.rail_bg'), dressChanged(perStudio[i]) && t('studioEditor.rail_dress'), toolsTitle(photoTools)].filter(Boolean).join(', ')}
                                        className="pointer-events-none absolute top-1 right-1 z-[2] px-1.5 py-0.5 rounded-md text-white text-[9px] font-bold leading-none shadow-sm group-hover:opacity-0 transition-opacity"
                                    >
                                        {t('bulkPhotoEdit.create.settingsCount', { n: fmtNum(settingsCount(i)) })}
                                    </span>
                                )}
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span
                                            data-no-marquee
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => removePhoto(i, e)}
                                            className={`absolute top-1 right-1 z-[3] w-[15px] h-[15px] rounded-full bg-black/55 text-white flex items-center justify-center transition-opacity cursor-pointer hover:bg-red-500 ${invalid ? 'opacity-100 bg-red-500' : 'opacity-0 group-hover:opacity-100'}`}
                                        >
                                            <IconX size={9} stroke={2.6} />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{t('bulkPhotoEdit.create.tipRemove')}</TooltipContent>
                                </Tooltip>
                            </button>
                            );
                        })}
                    </div>
                    {count > 0 && (
                        <div className="flex-shrink-0 flex items-stretch gap-1.5 w-full">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleSelectAll}
                                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-[11px] font-semibold text-zinc-600 dark:text-zinc-300 hover:border-primary hover:text-primary transition-colors"
                                    >
                                        {allSelected ? <IconSquareCheckFilled size={14} className="text-primary" /> : <IconSquare size={14} stroke={2} />}
                                        {allSelected
                                            ? t('bulkPhotoEdit.create.deselectAll')
                                            : selected.size > 0
                                                ? `${t('bulkPhotoEdit.create.selectAll')} (${fmtNum(selected.size)})`
                                                : t('bulkPhotoEdit.create.selectAll')}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">{t(allSelected ? 'bulkPhotoEdit.create.deselectAll' : 'bulkPhotoEdit.create.selectAll')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={selected.size ? removeSelected : clearAll}
                                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 hover:text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <IconTrash size={13} stroke={1.9} />
                                        {selected.size ? t('bulkPhotoEdit.create.deleteSelected', { n: fmtNum(selected.size) }) : t('bulkPhotoEdit.create.clearAll')}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">{t(selected.size ? 'bulkPhotoEdit.create.tipDeleteSelected' : 'bulkPhotoEdit.create.tipClearAll')}</TooltipContent>
                            </Tooltip>
                        </div>
                    )}
                    {/* Keyboard shortcuts hint — desktop left sidebar only */}
                    <div className="hidden xl:flex items-center justify-center gap-3 w-full flex-shrink-0 mt-auto pt-2.5 border-t border-zinc-100 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1">
                                    <IconUpload size={12} stroke={1.9} />
                                    <kbd className="px-1 py-0.5 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 text-[9px] font-medium">Ctrl+U</kbd>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{t('bulkPhotoEdit.create.addPhotos')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1">
                                    <IconSparkles size={12} stroke={1.9} />
                                    <kbd className="px-1 py-0.5 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 text-[9px] font-medium">Ctrl+G</kbd>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{t('bulkPhotoEdit.create.tipEnhance')}</TooltipContent>
                        </Tooltip>
                    </div>
                </div>

                {/* Inspector */}
                <aside className={`${RAIL_PANEL_CLS} ${panelCls('style')} xl:col-start-3 xl:row-start-1 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 xl:border-l flex-col min-h-0`}>
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                        <div className="flex-shrink-0 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-2">
                            <h2 className="min-w-0 flex-1 text-[14px] font-bold text-zinc-900 dark:text-zinc-100 truncate">{isCompact ? t(activeSection.labelKey) : t('bulkPhotoEdit.create.stylePresets')}</h2>
                            {selected.size > 0 && (
                                <span className="flex-shrink-0 text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5">
                                    {t('bulkPhotoEdit.create.selectedCountBadge', { n: fmtNum(selected.size) })}
                                </span>
                            )}
                            {panelCloseBtn}
                        </div>

                        {/* The Studio Editor's sections: background (scenes, colours, merge), dress, tools. The rail picks them below `xl`. */}
                        <div className="flex-shrink-0 px-4 pt-3 max-xl:hidden">
                            <div className="grid grid-cols-3 gap-1 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800" role="tablist">
                                {SECTIONS.map((sec) => (
                                    <button
                                        key={sec.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={inspectorTab === sec.key}
                                        onClick={() => setInspectorTab(sec.key)}
                                        className={`relative inline-flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-semibold transition-colors truncate ${inspectorTab === sec.key ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}
                                    >
                                        <sec.Icon size={14} stroke={1.9} />
                                        {t(sec.labelKey)}
                                        {sectionCount[sec.key] > 0 && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-2 text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">{t(activeSection.descKey)}</p>
                        </div>

                        <div className="relative flex-1 min-h-0 overflow-y-auto px-4 py-3">
                            {inspectorTab === 'bg' && (
                                <BackgroundPicker
                                    bg={pending.bg}
                                    onPick={(v) => setPending((p) => ({ ...p, bg: v }))}
                                    blend={pending.bgBlend}
                                    onBlendChange={(v) => setPending((p) => ({ ...p, bgBlend: v }))}
                                />
                            )}
                            {inspectorTab === 'dress' && (
                                <StudioDressSection
                                    people={pending.people}
                                    dresses={pending.dresses}
                                    onPeopleChange={(v) => setPending((p) => ({ ...p, people: v }))}
                                    onDressChange={(key, d) => setPending((p) => ({ ...p, dresses: { ...p.dresses, [key]: d } }))}
                                />
                            )}
                            {inspectorTab === 'tools' && (
                                <StudioToolsPanel value={pending.tools} onChange={(v) => setPending((p) => ({ ...p, tools: v }))} />
                            )}
                        </div>

                        {/* Clear / Apply — the panel's settings onto the selected photos; hint beside them */}
                        <div className="flex-shrink-0 border-t border-zinc-100 dark:border-zinc-800 px-4 py-2.5 flex items-center gap-1.5">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => setPending(EMPTY_SETTINGS)}
                                        disabled={pendingCount === 0}
                                        className="flex-shrink-0 h-9 px-3.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <IconX size={15} stroke={2.4} />
                                        {t('bulkPhotoEdit.create.clearTools')}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{t('bulkPhotoEdit.create.tipClearTools')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={applyTools}
                                        disabled={!selected.size || pendingCount === 0}
                                        className="flex-shrink-0 h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <IconCheck size={15} stroke={2.4} />
                                        {t('bulkPhotoEdit.create.applyBtn', { n: fmtNum(pendingCount) })}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{t('bulkPhotoEdit.create.tipApplyTools')}</TooltipContent>
                            </Tooltip>
                            {pendingCount > 0 && selected.size === 0 && (
                                <span className="flex-1 min-w-0 text-[10px] font-medium text-amber-600 dark:text-amber-400 leading-snug">
                                    {t('bulkPhotoEdit.create.selectImagesToApply')}
                                </span>
                            )}
                        </div>
                    </div>

                    {!isCompact && submitFooter}
                </aside>

                {isCompact && submitFooter}
            </div>

            {/* ── Re-style confirmation ── some selected photos already have a style ── */}
            <Dialog open={!!confirmRestyle} onOpenChange={(o) => { if (!o) setConfirmRestyle(null); }}>
                <DialogContent showClose={false} className="max-w-[420px] p-0 rounded-2xl overflow-hidden font-hind">
                    <div className="px-5 pt-5 pb-3.5 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-3">
                        <span className="w-[38px] h-[38px] flex-shrink-0 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                            <IconAlertTriangle size={19} stroke={2} />
                        </span>
                        <DialogTitle className="text-[15.5px] font-bold text-zinc-900 dark:text-zinc-100">{t('bulkPhotoEdit.create.restyleTitle')}</DialogTitle>
                    </div>
                    <div className="px-5 py-4">
                        <p className="text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                            {t('bulkPhotoEdit.create.restyleBody', { n: fmtNum(confirmRestyle?.count ?? 0) })}
                        </p>
                    </div>
                    <div className="px-5 pb-5 pt-1 flex gap-2.5">
                        <button
                            onClick={() => setConfirmRestyle(null)}
                            className="flex-1 h-11 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-[13.5px] font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                            {t('bulkPhotoEdit.create.cancel')}
                        </button>
                        <button
                            onClick={() => { writeTools(confirmRestyle.tools, confirmRestyle.levels, confirmRestyle.studio); setConfirmRestyle(null); }}
                            className="flex-1 h-11 rounded-xl bg-gradient-to-br from-primary to-[#c77400] text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-primary/30 hover:brightness-105 transition"
                        >
                            {t('bulkPhotoEdit.create.restyleConfirm')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Confirmation modal ── */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent showClose={false} className="max-w-[480px] p-0 rounded-2xl overflow-hidden font-hind">
                    <div className="px-5 pt-5 pb-3.5 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-3">
                        <span className="w-[38px] h-[38px] flex-shrink-0 rounded-xl bg-gradient-to-br from-primary to-[#c77400] text-white flex items-center justify-center">
                            <IconSparkles size={19} stroke={1.9} />
                        </span>
                        <div>
                            <DialogTitle className="text-[15.5px] font-bold text-zinc-900 dark:text-zinc-100">{t('bulkPhotoEdit.create.confirmTitle')}</DialogTitle>
                            <p className="text-[11.5px] text-zinc-400 dark:text-zinc-500 mt-0.5">{t('bulkPhotoEdit.create.confirmSubtitle')}</p>
                        </div>
                        <button
                            onClick={() => setConfirmOpen(false)}
                            aria-label={t('common.close') || 'বন্ধ করুন'}
                            className="ml-auto flex-shrink-0 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                        >
                            <IconX size={16} stroke={2.5} />
                        </button>
                    </div>

                    <div className="px-5 py-4">
                        {/* Project name — optional */}
                        <input
                            value={projectName}
                            onChange={(e) => { nameTouched.current = true; setProjectName(e.target.value); }}
                            placeholder={t('bulkPhotoEdit.create.namePlaceholder')}
                            className="w-full mb-3.5 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <div className="flex flex-col gap-px rounded-2xl border border-zinc-100 dark:border-zinc-800 overflow-hidden">
                            {[
                                { label: t('bulkPhotoEdit.create.totalImages'), value: t('bulkPhotoEdit.create.imagesCountValue', { n: fmtNum(validCount) }) },
                                { label: t('bulkPhotoEdit.create.perImageLabel'), value: t('bulkPhotoEdit.create.creditsValue', { n: fmtNum(perImageCost) }) },
                                { label: t('bulkPhotoEdit.create.estTime'), value: estLabel },
                            ].map((row) => (
                                <div key={row.label} className="flex items-center justify-between px-3.5 py-3 bg-zinc-50 dark:bg-zinc-800/50">
                                    <span className="text-[12.5px] text-zinc-500 dark:text-zinc-400">{row.label}</span>
                                    <span className="text-[13.5px] font-bold text-zinc-900 dark:text-zinc-100">{row.value}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between mt-3.5 px-0.5">
                            <span className="text-[13px] font-bold text-zinc-900 dark:text-zinc-100">{t('bulkPhotoEdit.create.totalSpend')}</span>
                            <span className="inline-flex items-baseline gap-1.5 text-[22px] font-extrabold text-primary">
                                {fmtNum(totalCost)}
                                <span className="text-[12px] font-semibold text-primary/80">{t('bulkPhotoEdit.create.creditsUnit')}</span>
                            </span>
                        </div>
                        {insufficientCredit && (
                            <p className="mt-2 px-0.5 text-[12px] font-semibold text-red-500 dark:text-red-400">
                                {t('bulkPhotoEdit.create.insufficientDetail', { have: fmtNum(availableCredit) })}
                            </p>
                        )}
                    </div>

                    {/* Irreversible-action warning */}
                    <div className="px-5">
                        <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3.5 py-3">
                            <IconAlertTriangle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" stroke={2} />
                            <p className="text-[12px] leading-relaxed text-amber-700 dark:text-amber-300">{t('bulkPhotoEdit.create.startWarning')}</p>
                        </div>
                    </div>

                    <div className="px-5 pb-5 pt-3.5 flex gap-2.5">
                        <button onClick={() => setConfirmOpen(false)} className="flex-shrink-0 h-11 px-5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-[13.5px] font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            {t('bulkPhotoEdit.create.cancel')}
                        </button>
                        {insufficientCredit ? (
                            <button
                                onClick={() => { setConfirmOpen(false); navigate(buyCreditPath); }}
                                className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-red-500/30 transition-colors"
                            >
                                <IconCreditCard size={16} stroke={1.9} />
                                {t('bulkPhotoEdit.create.rechargeCredit')}
                            </button>
                        ) : (
                            <button
                                onClick={doCreate}
                                disabled={submitting}
                                className="flex-1 h-11 rounded-xl bg-gradient-to-br from-primary to-[#c77400] text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-primary/30 disabled:opacity-60"
                            >
                                {submitting ? <IconLoader2 size={16} className="animate-spin" /> : <IconBolt size={16} stroke={1.9} />}
                                {t('bulkPhotoEdit.create.startEdit')}
                            </button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
